<script lang="ts">
  // @ts-ignore
  import Sortable from 'sortablejs';
  import { onMount } from 'svelte';
  import {createEventDispatcher} from 'svelte';
  import CurationListItem from './CurationListItem.svelte';
  import CurationQuestionSet from './CurationQuestionSet.svelte';
  import CurationSectionActions from './CurationSectionActions.svelte';
  import EditableRichText from '../shared/EditableRichText.svelte';
  import EditableTextArea from '../shared/EditableTextArea.svelte';
  import Icon from '$shared/icons/Icon.svelte';

  export let editable: boolean = false;
  export let parentId: number = 0;
  export let parentSortable: Sortable;
  export let sectionContents: any = [];
  export let sectionCuratableId: number = 0;
  export let sectionDescription = '';
  export let sectionDuration: string = '00:00:00';
  export let sectionTitle = '';
  export let sectionId = 1;
  export let learningObjectivesHeader = 'Learning Objectives';

  export let sectionDescriptionPlaceholder = 'Click to edit section description';
  export let sectionDragPlaceholder = 'Drag and drop content here.';
  export let sectionDurationLabel = 'Duration';
  export let sectionTitlePlaceholder = 'Click to edit section name';

  export let sectionActionsMoveTopLabel = 'Move Section To Top';
  export let sectionActionsMoveUpLabel = 'Move Section Up';
  export let sectionActionsMoveDownLabel = 'Move Section Down';
  export let sectionActionsMoveBottomLabel = 'Move Section To Bottom';
  export let sectionActionsDeleteLabel = 'Delete Section';

  export let sectionDeleteConfirmationText = 'Are you sure you want to delete this section, including the items within it?';
  export let itemDeleteConfirmationText = 'Are you sure you want to delete this item?';
  export let questionSetText = {};

  let sectionContentsList: HTMLDivElement;

  let sectionSortable: any;
  let sectionLength = sectionContents ? sectionContents.length : 0;

  $: parentLength = parentSortable?.toArray().length;

  const dispatch = createEventDispatcher();

  onMount(async function () {
    sectionSortable = Sortable.create(sectionContentsList, {
      group: {
        name: 'sectionContentsList',
        put: true,
        pull: true
      },
      animation: 200,
      filter: '#addnew', // prevent Add New bar from being sorted
      handle: `.handle`,
      // Element dragging ended
      onEnd: function (/**Event*/ evt: any) {
        let itemEl = evt.item.getAttribute('data-id');
        let fromCuration = evt.from.getAttribute('data-curatable-id');
        let toCuration = evt.to.getAttribute('data-curatable-id');
        if (toCuration === null) {
          // The above works when dragging things into a section or between sections
          // The parentId works when dragging into the main curation out of a section
          toCuration = parentId;
        }
        let newIndex = evt.newIndex; // element's new index within new parent

        handleSort(itemEl, newIndex, fromCuration, toCuration);
      },
      onRemove: function () {
        sectionLength = sectionSortable.toArray().length;
      },
      onAdd: function () {
        sectionLength = sectionSortable.toArray().length;
      }
    });
  });

  function handleSort(itemEl: number, newIndex: number, fromCuration: number, toCuration: number) {
    fetch(`/curations/${fromCuration}/curated_items/${itemEl}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      // @ts-ignore
      headers: {
        'Content-Type': 'application/json',
        // @ts-ignore
        'X-CSRF-Token': document
        .querySelector('meta[name="csrf-token"]')
        .getAttribute('content')
      },
      body: JSON.stringify({
        position: newIndex,
        to_curation: toCuration
      })
    });
  }

  function handleMoveBy(increment:1 | -1 | 0 = 0){
    let order = parentSortable.toArray();
    let currentItem = sectionId.toString();
    let sorted = moveItem(order.indexOf(currentItem), order.indexOf(currentItem) + increment, order);
    let newIndex = currentIndex + increment;

    parentSortable.sort(sorted, true);
    handleSort(sectionId, newIndex, parentId, parentId);
    dispatch('move', { id: sectionId, curatable: sectionCuratableId });
  }
  function handleMoveToTop() {
    let order = parentSortable.toArray();
    let currentItem = sectionId.toString();
    let sorted = moveItem(order.indexOf(currentItem), 0, order);
    let newIndex = 0;

    parentSortable.sort(sorted, true);
    handleSort(sectionId, newIndex, parentId, parentId);
    dispatch('move', { id: sectionId, curatable: sectionCuratableId });
  }
  function handleMoveToBottom() {
    let order = parentSortable.toArray();
    let currentItem = sectionId.toString();
    let sorted = moveItem(order.indexOf(currentItem), order.length, order);
    let newIndex = order.length;

    parentSortable.sort(sorted, true);
    handleSort(sectionId, newIndex, parentId, parentId);
    dispatch('move', { id: sectionId, curatable: sectionCuratableId });
  }

  function handleRemovingSection() {
    const response = confirm(sectionDeleteConfirmationText);

    if (response == true) {
      fetch(`/curations/${parentId}/curated_items/${sectionId}`, {
        method: "DELETE",
        credentials: "same-origin",
        // @ts-ignore
        headers: {
          "Content-Type": "application/json",
          // @ts-ignore
          "X-CSRF-Token": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
        },
        body: JSON.stringify({
          curatable_id: sectionCuratableId,
        })
      });

      const item = document.querySelector(`[data-id="${sectionId}"]`);
      if (item) item.remove();
    } else {
      return;
    }
  }

  $: currentIndex = getCurrentIndex(parentSortable);

  function getCurrentIndex(parent:any){
    if(!parent) return;
    return parent.toArray().indexOf(sectionId.toString());
  }

  function moveItem(from: number, to: number, data: any) {
    // This handles the appearance of moving the sections around on the page
    var f = data.splice(from, 1)[0];
    data.splice(to, 0, f);

    return data;
  }
</script>

<div
  data-id={sectionId}
  data-level={1}
  class="border border-neutral-200 p-4 rounded flex flex-col gap-3 bg-white mx-5 relative"
>
  {#if editable}
    <div class="handle-button handle">
      <Icon icon="GripVertical" size="medium" />
    </div>
  {/if}
  <div class="mb-4 pb-2 border-b border-neutral-200">
    <div class="w-full mb-2 flex justify-between">
      <p class="text-lg font-semibold max-w-lg flex-grow mr-4">
        <EditableTextArea action={`/curations/${sectionCuratableId}`}
          contents={sectionTitle} contentStyle="w-full" editable={editable}
          model="curation" placeholder={sectionTitlePlaceholder} property="name" />
      </p>
      <div class="flex flex-wrap justify-end gap-2 items-center mb-2">
        <p class="text-xs italic mr-2 text-right">{sectionDurationLabel}: <span data-section-duration>{sectionDuration}</span></p>
        {#if editable}
          <CurationSectionActions
            isFirst={currentIndex === 0}
            isLast={parentLength === currentIndex + 1}
            on:moveUp={()=>handleMoveBy(-1)}
            on:moveDown={()=>handleMoveBy(1)}
            on:moveTop={()=>handleMoveToTop()}
            on:moveBottom={()=>handleMoveToBottom()}
            on:delete={handleRemovingSection}
            id={sectionId}
            curatable={sectionCuratableId}
            moveTopLabel={sectionActionsMoveTopLabel}
            moveUpLabel={sectionActionsMoveUpLabel}
            moveDownLabel={sectionActionsMoveDownLabel}
            moveBottomLabel={sectionActionsMoveBottomLabel}
            deleteLabel={sectionActionsDeleteLabel}
          />
        {/if}
      </div>
    </div>
    <div class="w-full max-w">
      <EditableRichText
        action={`/curations/${sectionCuratableId}`}
        contents={sectionDescription}
        editable={editable}
        model="curation"
        placeholder={sectionDescriptionPlaceholder}
        property="description"
      />
    </div>
  </div>
  <div
    bind:this={sectionContentsList}
    data-id={sectionId}
    data-curatable-id={sectionCuratableId}
    class:empty={sectionLength === 0}
    class="flex flex-col gap-3 py-2 w-full bg-white"
    >
    {#if sectionContents && sectionContents.length}
      {#each sectionContents as item}
        {#if item.curatable_type === 'QuestionSet'}
          <!-- Learners see the closed card, which they can't open until BIG-401. -->
          <CurationQuestionSet
            id={item.id}
            curationId={item.curation_id}
            questionSetId={item.curatable_id}
            title={item.title}
            description={item.description}
            durationSeconds={item.duration_seconds}
            initialQuestions={item.questions ?? []}
            responseCount={item.response_count ?? 0}
            questionCount={item.question_count ?? 0}
            {editable}
            {...questionSetText}
          />
        {:else}
        <CurationListItem
          curatableId={item.curatable_id}
          curatableType={item.curatable_type}
          duration={item.duration}
          editable={editable}
          externalUrl={item.url}
          href={item.href}
          id={item.id}
          itemDeleteConfirmationText={itemDeleteConfirmationText}
          learning_objectives={item.learning_objectives}
          learningObjectivesHeader={learningObjectivesHeader}
          parent={item.curation_id}
          progress={item.progress}
          subhead={item.expert_name}
          subeyebrow={item.expert_title}
          thumbnail_url={item.thumbnail_url}
          title={item.title}
          type_overlay={item.type_overlay}
          updateable={item.updateable}
        />
        {/if}
      {/each}
    {/if}

    {#if sectionLength === 0}
      <div class="flex items-center justify-center">
        <p class="text-neutral-500 text-sm text-center italic">{sectionDragPlaceholder}</p>
      </div>
    {/if}
  </div>
</div>

<style>
  .empty {
    min-height: 100px;
    display: flex;
    align-items: center;
    position: relative;
    justify-content: center;
    border: 2px dashed #dadada;
  }
  .handle-button {
    width: theme('size.8');
    height: theme('size.8');
    cursor: pointer;
    display: grid;
    place-content: center;
    position: absolute;
    top: theme('size.2');
    left: calc(theme('size.8') * -1);
    cursor: grab;
    border-right: 1px solid transparent;
    &:hover {
      background: theme('colors.neutral.100');
      border-right: 1px solid theme('colors.neutral.900');
    }
    &:active {
      cursor: grabbing;
    }
  }
</style>
